import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { groupBy } from 'lodash';
import { useState } from 'react';

import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';
import useHandleError from '@/hooks/useHandleError';
import usePermissionsCheck from '@/hooks/usePermissionsCheck';
import {
  orgProductsAtom,
  selectedOrganizationAtom
} from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';

import ConsentTile from './ConsentTile';
import {
  ASSET_INVENTORY_TILE,
  MICROSOFT_STEPS
} from './MsDefenderIntegration.constants';
import { useFetchLegacyLimaConnections } from './MsDefenderIntegration.hooks';
import { msDefenderIntegrationStyles } from './MsDefenderIntegration.styles';
import type {
  ConsentTarget,
  ConsentTileConfig,
  DisconnectTarget,
  IntegrationDeleteResponse,
  MicrosoftIntegration,
  PendingConsent
} from './MsDefenderIntegration.types';
import {
  getCascadeTitles,
  getCategoryStateByTenant,
  getConsentEligibility,
  getDisconnectDescription,
  groupIntegrationsByAppType,
  hasConnectedDefenderIngestion
} from './MsDefenderIntegration.utils';

const MsDefenderIntegration = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [activeProducts] = useAtom(orgProductsAtom);
  const hasAdminUpdate = usePermissionsCheck(PERMISSIONS.ADMIN_UPDATE);
  const hasAdminRead = usePermissionsCheck(PERMISSIONS.ADMIN_READ);
  const queryClient = useQueryClient();
  const handleError = useHandleError();

  const [disconnectTarget, setDisconnectTarget] =
    useState<DisconnectTarget | null>(null);
  const [pendingConsent, setPendingConsent] = useState<PendingConsent | null>(
    null
  );
  const [consentErrorTarget, setConsentErrorTarget] =
    useState<PendingConsent | null>(null);
  const [disconnectErrorIntegrationId, setDisconnectErrorIntegrationId] =
    useState<number | null>(null);

  const integrationsQueryKey = [
    `organizations/${selectedOrganization?.label}/integrations?integration_type=microsoft`
  ];

  const {
    data: integrations,
    isError: isIntegrationsError,
    isLoading
  } = useQuery<MicrosoftIntegration[]>({
    queryKey: integrationsQueryKey,
    enabled:
      !!selectedOrganization?.label && selectedOrganization.label !== 'all'
  });

  const rawIntegrations = integrations ?? [];
  const integrationsByAppType = groupIntegrationsByAppType(integrations);
  const { data: legacyConnections, isLoading: isLegacyConnectionsLoading } =
    useFetchLegacyLimaConnections(selectedOrganization?.label, hasAdminRead);
  const legacyConnectionsByAppType = groupBy(
    legacyConnections ?? [],
    // The endpoint returns organization-scoped legacy parents; each belongs to one consent tile.
    (connection) => connection.app_type
  );
  const hasAnyDefenderIngestion = rawIntegrations.some((integration) =>
    hasConnectedDefenderIngestion(rawIntegrations, integration.tenant_id)
  );
  const isServerStateAvailable =
    !isIntegrationsError && integrations !== undefined;

  const consentMutation = useMutation({
    mutationFn: ({ targetTile }: ConsentTarget) =>
      axiosClient
        .get<string>(
          `/organizations/${selectedOrganization?.label}/integrations/microsoft/consent_url`,
          {
            params: {
              app_name: targetTile.appType
            }
          }
        )
        .then((response) => response.data),
    onError: (error: AxiosError, target: ConsentTarget) => {
      setConsentErrorTarget({
        appType: target.targetTile.appType,
        existingIntegrationId: target.existingIntegration?.id
      });
      handleError(error, 'Failed to start Microsoft consent.');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (integrationId: number) =>
      axiosClient
        .delete<IntegrationDeleteResponse>(
          `/organizations/${selectedOrganization?.label}/integrations/${integrationId}`
        )
        .then((response) => response.data),
    onSuccess: ({ deleted }) => {
      queryClient.setQueryData<MicrosoftIntegration[] | undefined>(
        integrationsQueryKey,
        (current) => current?.filter((row) => !deleted.includes(row.id))
      );
      void queryClient.invalidateQueries({ queryKey: integrationsQueryKey });
      setDisconnectTarget(null);
      setDisconnectErrorIntegrationId(null);
    },
    onError: (error: AxiosError, integrationId: number) => {
      setDisconnectErrorIntegrationId(integrationId);
      handleError(error, 'Failed to disconnect integration.');
      setDisconnectTarget(null);
    }
  });

  const openConsent = async (target: ConsentTarget) => {
    if (!selectedOrganization?.label) return;

    const consentWindow = window.open('', '_blank');
    if (consentWindow) consentWindow.opener = null;

    const nextPendingConsent = {
      appType: target.targetTile.appType,
      existingIntegrationId: target.existingIntegration?.id
    };
    setConsentErrorTarget(null);
    setPendingConsent(nextPendingConsent);

    try {
      const url = await consentMutation.mutateAsync(target);
      if (consentWindow) {
        consentWindow.location.replace(url);
      } else {
        window.location.assign(url);
      }
    } catch {
      consentWindow?.close();
    } finally {
      setPendingConsent(null);
    }
  };

  const handleDisconnectConfirm = () => {
    if (disconnectTarget)
      deleteMutation.mutate(disconnectTarget.integration.id);
  };

  const renderTile = (tile: ConsentTileConfig) => {
    const appConnections = integrationsByAppType[tile.appType] ?? [];
    const legacyConnections = legacyConnectionsByAppType[tile.appType] ?? [];
    const consentEligibility = getConsentEligibility(tile, activeProducts);

    return (
      <ConsentTile
        tile={tile}
        connections={appConnections}
        legacyConnections={legacyConnections}
        allIntegrations={rawIntegrations}
        categoryStateByTenant={getCategoryStateByTenant(
          integrations,
          tile.appType
        )}
        hasAdminUpdate={hasAdminUpdate}
        isServerStateAvailable={isServerStateAvailable}
        hasAnyDefenderIngestion={hasAnyDefenderIngestion}
        consentEligibility={consentEligibility}
        pendingConsent={pendingConsent}
        consentErrorTarget={consentErrorTarget}
        onConsent={(target) => void openConsent(target)}
        onDisconnect={(integration) => {
          setDisconnectErrorIntegrationId(null);
          setDisconnectTarget({ tile, integration });
        }}
        disconnectingIntegrationId={
          deleteMutation.isPending
            ? disconnectTarget?.integration.id
            : undefined
        }
        disconnectErrorIntegrationId={disconnectErrorIntegrationId ?? undefined}
      />
    );
  };

  const cascadeTitles = getCascadeTitles(disconnectTarget?.tile ?? null);
  const disconnectDescription = getDisconnectDescription(cascadeTitles);

  return (
    <LargeCard title="Microsoft">
      {isIntegrationsError && (
        <Alert
          severity="error"
          variant="outlined"
          sx={msDefenderIntegrationStyles.queryError}
        >
          Unable to load Microsoft tenant connections. Connection actions are
          unavailable until the data refreshes.
        </Alert>
      )}

      {(isLoading || isLegacyConnectionsLoading) && (
        <Box sx={msDefenderIntegrationStyles.loadingState}>
          <CircularProgress size={20} />
          <Typography color="text.secondary" variant="body2">
            Loading tenant connections...
          </Typography>
        </Box>
      )}

      {MICROSOFT_STEPS.map((step) => (
        <Box key={step.category} sx={{ pb: 4 }}>
          <Box sx={{ pb: 1 }}>
            <Typography sx={msDefenderIntegrationStyles.stepLabel}>
              {step.label}
            </Typography>
            <Typography variant="h5">{step.heading}</Typography>
            <Typography variant="subtitle2" color="textSecondary">
              {step.instruction}
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
            {step.options.map((tile) => (
              <Grid
                size={{ xs: 12, lg: 6 }}
                key={tile.appType}
                sx={msDefenderIntegrationStyles.optionGridItem}
              >
                {renderTile(tile)}
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      <Box sx={{ pb: 1 }}>
        <Typography sx={msDefenderIntegrationStyles.stepLabel}>
          ASSET INVENTORY
        </Typography>
        <Typography variant="h5">
          Enable Scope to list Microsoft Defender for Endpoint device and assets
        </Typography>
        <Typography variant="subtitle2" color="textSecondary">
          Connect your tenant to allow Pondurance to read Defender for Endpoint
          device and asset inventory.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid
          size={{ xs: 12, lg: 6 }}
          sx={msDefenderIntegrationStyles.optionGridItem}
        >
          {renderTile(ASSET_INVENTORY_TILE)}
        </Grid>
      </Grid>

      <Dialog
        open={!!disconnectTarget}
        onClose={() => setDisconnectTarget(null)}
      >
        <DialogTitle>
          Disconnect {disconnectTarget?.tile.title} for tenant{' '}
          {disconnectTarget?.integration.tenant_id ?? 'this tenant'}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{disconnectDescription}</DialogContentText>
          {cascadeTitles.length > 0 && (
            <DialogContentText sx={{ fontWeight: 600, mt: 1 }}>
              This will also disconnect {cascadeTitles.join(', ')} for this
              tenant only. Other tenants remain connected.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDisconnectConfirm}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </LargeCard>
  );
};

export default MsDefenderIntegration;
